import { motion } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import {
  Lock,
  Crown,
  BookOpen,
  Brain,
  Newspaper,
  Sparkles,
  ChevronRight,
} from "lucide-react";

interface PremiumUpsellProps {
  tier: "starter" | "execution" | "professional" | "elite";
  title?: string;
  subtitle?: string;
  compact?: boolean;
}

const TIER_CONFIG = {
  starter: {
    name: "Starter",
    price: "R299",
    description: "Unlock beginner courses and structured learning paths",
    benefits: [
      { icon: BookOpen, text: "Beginner Trader Path" },
      { icon: Newspaper, text: "Basic Market Breakdowns" },
    ],
    color: "bg-emerald-500/10 text-emerald-600 border-emerald-500/20",
    buttonColor: "bg-emerald-500 hover:bg-emerald-600 text-white",
  },
  execution: {
    name: "Execution",
    price: "R799",
    description: "Intermediate strategies, full AI Coach, and case studies",
    benefits: [
      { icon: BookOpen, text: "Intermediate Path" },
      { icon: Brain, text: "AI Coach Access" },
      { icon: Newspaper, text: "Full Market Breakdowns" },
    ],
    color: "bg-amber-500/10 text-amber-600 border-amber-500/20",
    buttonColor: "bg-amber-500 hover:bg-amber-600 text-white",
  },
  professional: {
    name: "Professional",
    price: "R1999",
    description: "Complete professional framework and advanced psychology",
    benefits: [
      { icon: Crown, text: "Professional Framework" },
      { icon: Brain, text: "Advanced Psychology" },
      { icon: Sparkles, text: "Priority AI Support" },
    ],
    color: "bg-violet-500/10 text-violet-600 border-violet-500/20",
    buttonColor: "bg-violet-500 hover:bg-violet-600 text-white",
  },
  elite: {
    name: "Mentorship",
    price: "R249/month",
    description: "Weekly mentorship calls and private community",
    benefits: [
      { icon: Crown, text: "Weekly Live Calls" },
      { icon: Brain, text: "1-on-1 Strategy Reviews" },
      { icon: Sparkles, text: "Private Community" },
    ],
    color: "bg-amber-400/10 text-amber-700 border-amber-400/20",
    buttonColor: "bg-amber-500 hover:bg-amber-600 text-white",
  },
};

export default function PremiumUpsell({
  tier,
  title,
  subtitle,
  compact = false,
}: PremiumUpsellProps) {
  const navigate = useNavigate();
  const config = TIER_CONFIG[tier];

  if (compact) {
    return (
      <Card className="p-4 rounded-2xl border-border/50 bg-gradient-to-br from-card/80 to-secondary/20">
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${config.color}`}>
            <Lock className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-display font-semibold text-sm truncate">{title || `Unlock ${config.name}`}</h3>
            <p className="text-xs text-muted-foreground truncate">{subtitle || config.description}</p>
          </div>
          <Button
            size="sm"
            className={`rounded-lg gap-1 ${config.buttonColor}`}
            onClick={() => navigate("/membership")}
          >
            {config.price} <ChevronRight className="h-3 w-3" />
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card className="p-5 rounded-2xl border-border/60">
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${config.color}`}>
              <Lock className="h-6 w-6" />
            </div>
            <div>
              <h3 className="font-display font-semibold text-lg">{title || `${config.name} Content Locked`}</h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
                {subtitle || config.description}
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-2">
            {config.benefits.map((b, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                <b.icon className="h-4 w-4 text-primary/70" />
                {b.text}
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3">
            <Button
              className={`rounded-xl gap-1.5 ${config.buttonColor}`}
              onClick={() => navigate("/membership")}
            >
              Unlock {config.name} — {config.price}
            </Button>
            <Button variant="ghost" className="text-muted-foreground" onClick={() => navigate("/membership")}>
              Learn more
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
